import { useMemo } from "react";
import type { MazeRunnerProps, Direction } from "./types";

/**
 * MazeRunner Component
 *
 * The character that moves through the maze. Renders as a friendly robot
 * owl (Pixel's placeholder) with smooth CSS animations for movement,
 * collection, and victory states.
 */
export function MazeRunner({
  position,
  direction,
  isMoving = false,
  isCollecting = false,
  hasWon = false,
  cellSize,
  gridGap,
}: MazeRunnerProps) {
  // Calculate pixel position based on grid cell
  const { top, left } = useMemo(() => {
    const [row, col] = position;
    return {
      top: row * (cellSize + gridGap),
      left: col * (cellSize + gridGap),
    };
  }, [position, cellSize, gridGap]);

  // Rotation based on direction
  const rotation = useMemo(() => {
    const rotations: Record<Direction, number> = {
      up: 0,
      right: 90,
      down: 180,
      left: 270,
    };
    return rotations[direction];
  }, [direction]);

  return (
    <div
      className={`
        absolute
        flex items-center justify-center
        pointer-events-none
        z-10
        transition-all duration-300 ease-bounce
        ${isMoving ? "scale-110" : ""}
        ${isCollecting ? "animate-star-pop" : ""}
        ${hasWon ? "animate-celebrate" : ""}
      `}
      style={{
        width: cellSize,
        height: cellSize,
        top,
        left,
      }}
      role="img"
      aria-label={`Robot owl at row ${position[0] + 1}, column ${position[1] + 1}, facing ${direction}`}
    >
      {/* Robot Owl Character Container */}
      <div
        className={`
          relative w-[85%] h-[85%]
          transition-transform duration-200 ease-out
        `}
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        {/* Robot Owl Body */}
        <div
          className={`
            absolute inset-0
            rounded-[40%_40%_45%_45%]
            bg-gradient-to-b from-pixel-light via-pixel to-pixel-dark
            border-3 border-pixel-dark
            shadow-[0_4px_0_0_theme(colors.pixel.dark),0_6px_16px_rgba(255,140,66,0.4)]
            ${isMoving ? "animate-pulse" : ""}
          `}
        >
          {/* Metallic belly plate */}
          <div
            className="
              absolute bottom-[15%] left-[15%] right-[15%] h-[35%]
              rounded-[50%_50%_45%_45%]
              bg-gradient-to-b from-neutral-200 via-neutral-300 to-neutral-400
              border-2 border-neutral-400
            "
          >
            {/* Circuit pattern lines */}
            <div className="absolute top-1/4 left-1/4 right-1/4 h-px bg-pixel-accent/40" />
            <div className="absolute top-1/2 left-1/4 right-1/4 h-px bg-pixel-accent/40" />
            <div className="absolute top-3/4 left-1/4 right-1/4 h-px bg-pixel-accent/40" />
          </div>

          {/* Gear decoration - left */}
          <div
            className={`
              absolute top-[20%] left-[-8%]
              w-[22%] h-[22%]
              rounded-full
              bg-gradient-to-br from-neutral-300 to-neutral-500
              border border-neutral-600
              ${isMoving ? "animate-spin-slow" : ""}
            `}
          >
            <div className="absolute inset-[20%] rounded-full bg-neutral-400" />
          </div>

          {/* Gear decoration - right */}
          <div
            className={`
              absolute top-[20%] right-[-8%]
              w-[22%] h-[22%]
              rounded-full
              bg-gradient-to-br from-neutral-300 to-neutral-500
              border border-neutral-600
              ${isMoving ? "animate-spin-slow-reverse" : ""}
            `}
          >
            <div className="absolute inset-[20%] rounded-full bg-neutral-400" />
          </div>
        </div>

        {/* Eyes Container */}
        <div
          className="
            absolute top-[15%] left-[10%] right-[10%]
            flex justify-center gap-[15%]
          "
        >
          {/* Left Eye */}
          <div
            className={`
              relative w-[35%] aspect-square
              rounded-full
              bg-gradient-to-b from-neutral-800 via-neutral-900 to-black
              border-2 border-neutral-600
              shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]
              flex items-center justify-center
              overflow-hidden
            `}
          >
            {/* LED iris */}
            <div
              className={`
                w-[60%] h-[60%] rounded-full
                bg-gradient-to-br from-pixel-accent via-pixel-accent to-sky-600
                shadow-[0_0_8px_rgba(0,212,255,0.6)]
                ${hasWon ? "animate-pulse" : ""}
              `}
            >
              {/* Pupil */}
              <div className="absolute inset-[30%] rounded-full bg-black/60" />
              {/* Highlight */}
              <div className="absolute top-[15%] left-[15%] w-[25%] h-[25%] rounded-full bg-white/80" />
            </div>
          </div>

          {/* Right Eye */}
          <div
            className={`
              relative w-[35%] aspect-square
              rounded-full
              bg-gradient-to-b from-neutral-800 via-neutral-900 to-black
              border-2 border-neutral-600
              shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]
              flex items-center justify-center
              overflow-hidden
            `}
          >
            {/* LED iris */}
            <div
              className={`
                w-[60%] h-[60%] rounded-full
                bg-gradient-to-br from-pixel-accent via-pixel-accent to-sky-600
                shadow-[0_0_8px_rgba(0,212,255,0.6)]
                ${hasWon ? "animate-pulse" : ""}
              `}
            >
              {/* Pupil */}
              <div className="absolute inset-[30%] rounded-full bg-black/60" />
              {/* Highlight */}
              <div className="absolute top-[15%] left-[15%] w-[25%] h-[25%] rounded-full bg-white/80" />
            </div>
          </div>
        </div>

        {/* Beak */}
        <div
          className="
            absolute top-[48%] left-1/2 -translate-x-1/2
            w-0 h-0
            border-l-[10%] border-l-transparent
            border-r-[10%] border-r-transparent
            border-t-[15%] border-t-sunshine-500
            drop-shadow-sm
          "
          style={{
            borderLeftWidth: "clamp(6px, 10%, 12px)",
            borderRightWidth: "clamp(6px, 10%, 12px)",
            borderTopWidth: "clamp(8px, 15%, 16px)",
          }}
        />

        {/* Antenna */}
        <div
          className={`
            absolute top-[-15%] left-1/2 -translate-x-1/2
            w-[6%] h-[20%]
            bg-gradient-to-b from-neutral-400 to-neutral-600
            rounded-full
            ${isMoving ? "animate-wiggle" : ""}
          `}
        >
          {/* Antenna bulb */}
          <div
            className={`
              absolute -top-[40%] left-1/2 -translate-x-1/2
              w-[200%] aspect-square
              rounded-full
              bg-gradient-to-b from-pixel-accent to-pixel-accent
              shadow-[0_0_8px_rgba(0,212,255,0.8)]
              ${isMoving || hasWon ? "animate-pulse" : ""}
            `}
          />
        </div>

        {/* Wing feathers (ear tufts) */}
        <div
          className="
            absolute top-[5%] left-[-5%]
            w-[20%] h-[25%]
            bg-gradient-to-br from-pixel-light to-pixel-dark
            rounded-tl-[80%] rounded-tr-[20%]
            rounded-bl-[20%] rounded-br-[50%]
            transform -rotate-12
            border border-pixel-dark
          "
        />
        <div
          className="
            absolute top-[5%] right-[-5%]
            w-[20%] h-[25%]
            bg-gradient-to-bl from-pixel-light to-pixel-dark
            rounded-tl-[20%] rounded-tr-[80%]
            rounded-bl-[50%] rounded-br-[20%]
            transform rotate-12
            border border-pixel-dark
          "
        />
      </div>

      {/* Collection sparkle effect */}
      {isCollecting && (
        <div className="absolute inset-0 pointer-events-none">
          <span className="absolute top-0 left-1/2 text-xl animate-sparkle">✨</span>
          <span
            className="absolute top-1/2 right-0 text-xl animate-sparkle"
            style={{ animationDelay: "0.1s" }}
          >
            ⭐
          </span>
          <span
            className="absolute bottom-0 left-1/2 text-xl animate-sparkle"
            style={{ animationDelay: "0.2s" }}
          >
            ✨
          </span>
          <span
            className="absolute top-1/2 left-0 text-xl animate-sparkle"
            style={{ animationDelay: "0.3s" }}
          >
            ⭐
          </span>
        </div>
      )}

      {/* Victory confetti */}
      {hasWon && (
        <div className="absolute inset-[-50%] pointer-events-none">
          <span className="absolute top-0 left-1/4 text-2xl animate-confetti-1">🎉</span>
          <span className="absolute top-0 right-1/4 text-2xl animate-confetti-2">🎊</span>
          <span className="absolute bottom-1/4 left-0 text-xl animate-confetti-3">⭐</span>
          <span className="absolute bottom-1/4 right-0 text-xl animate-confetti-4">✨</span>
        </div>
      )}
    </div>
  );
}

export default MazeRunner;
